import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Eye, EyeOff } from 'lucide-react';
import PasswordValidationFeedback from '../../common/PasswordValidationFeedback';

const UserDetailsStep = ({ onRegister, onBack, error, loading, email }) => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    country: '',
    birthYear: '',
    password: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  const [birthYearError, setBirthYearError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const countries = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 
    'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 
    'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 
    'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 
    'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 
    'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 
    'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 
    'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 
    'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 
    'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 
    'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 
    'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 
    'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 
    'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 
    'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 
    'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 
    'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 
    'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Saudi Arabia', 'Senegal', 'Serbia', 
    'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 
    'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 
    'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 
    'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 
    'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()\-_+=\[\]{}|:;"'<>,.~`])[A-Za-z\d@$!%*?&#^()\-_+=\[\]{}|:;"'<>,.~`]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateBirthYear = (birthYear) => {
    if (!birthYear) return { isValid: false, error: 'Birth year is required' };
    
    const year = parseInt(birthYear);
    const currentYear = new Date().getFullYear();
    
    // Check if year is in the future
    if (year > currentYear) {
      return { isValid: false, error: 'Birth year cannot be in the future' };
    }
    
    // Calculate age
    const age = currentYear - year;
    
    // Check minimum age requirement (13 years old)
    if (age < 13) {
      return { isValid: false, error: 'You must be at least 13 years old to register' };
    }
    
    // Check maximum reasonable age (120 years old)
    if (age > 120) {
      return { isValid: false, error: 'Please enter a valid birth year' };
    }
    
    return { isValid: true, error: null };
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'password') {
      if (!validatePassword(value)) {
        setPasswordError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      } else {
        setPasswordError(null);
      }
    }
    
    if (name === 'birthYear') {
      const validation = validateBirthYear(value);
      setBirthYearError(validation.error);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const { name, lastName, country, birthYear, password } = formData;

    // Validate password
    if (!validatePassword(password)) {
      setPasswordError('Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    // Validate birth year
    const birthYearValidation = validateBirthYear(birthYear);
    if (!birthYearValidation.isValid) {
      setBirthYearError(birthYearValidation.error);
      return;
    }

    const userData = { name, lastName, email, country, birthYear: parseInt(birthYear), password };
    onRegister(userData);
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Your Registration</h3>
        <p className="text-sm text-gray-600">
          Please fill in your personal details to complete registration
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            First Name
          </label>
          <input
            type="text"
            name="name"
            id="name"
            value={formData.name}
            onChange={onChange}
            placeholder="Enter your first name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
            required
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            id="lastName"
            value={formData.lastName}
            onChange={onChange}
            placeholder="Enter your last name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
            required
          />
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
            Country
          </label>
          <select
            name="country"
            id="country"
            value={formData.country}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200"
            onChange={onChange}
            required
          >
            <option value="">Select your country</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="birthYear" className="block text-sm font-medium text-gray-700 mb-2">
            Birth Year
          </label>
          <select
            name="birthYear"
            id="birthYear"
            value={formData.birthYear}
            onChange={onChange}
            className={`w-full px-3 py-2 border ${birthYearError ? 'border-red-300' : 'border-gray-300'} rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200`}
            required
          >
            <option value="">Select year</option>
            {Array.from({ length: 121 }, (_, i) => new Date().getFullYear() - i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-blue-600">
            We ask for your birth year to confirm eligibility and apply the right privacy protections.
          </p>
          {birthYearError && (
            <p className="mt-2 text-sm text-red-600">
              {birthYearError}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              value={formData.password}
              onChange={onChange}
              placeholder="Enter your password"
              className={`w-full px-3 py-2 pr-12 border ${passwordError ? 'border-red-300' : 'border-gray-300'} rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors duration-200`}
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <PasswordValidationFeedback password={formData.password} />
          {passwordError && (
            <p className="mt-2 text-sm text-red-600">
              {passwordError}
            </p>
          )}
        </div>

        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-3 px-4 bg-gray-300 hover:bg-gray-400 text-gray-700 font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-3 px-4 bg-[#1086a4] hover:bg-[#0c6d85] disabled:bg-gray-400 text-white font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2 transition-colors duration-200 shadow-sm"
          >
            {loading ? 'Registering...' : 'Complete Registration'}
          </button>
        </div>
      </form>
    </div>
  );
};

UserDetailsStep.propTypes = {
  onRegister: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool,
  email: PropTypes.string.isRequired
};

export default UserDetailsStep;